import React, { useContext } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { connect } from "react-redux";
import { constructCtaProductData, getProductName } from "../../utils/shop";
import { showCart, addToCartRequest } from "../../actions/ShopActions";
import { EVENT_PROPERTIES_VALUES } from "../../types/Amplitude";
import { HATCH_PRODUCT_HANDLES } from "../../utils/productConstants";

// Components
import Button from "../Atoms/Button";
import Link from "../Atoms/Link";
import { TrackingContext } from "../Utils/TrackingWrapper";
import { useHatchProductCollection } from "../Utils/HatchProductCollection";
import { withBuilder } from "@builder.io/react";

export const onClick =
  ({
    productShopifyData,
    variantId,
    checkoutId,
    quantity,
    addToCart,
    showCart,
    trackAddToShopifyCart,
    trackOpenCart,
    trackViewCart,
    pageUrl,
    pageVariant,
    trackingProps,
    checkout,
  }) =>
  () => {
    const productData = constructCtaProductData(productShopifyData, variantId);

    //JGunther 4/5/2022 Hatch sleep membership is limited to 1 quantity. Do not increase quantity if it is already in the cart
    let skipAddToCart = false;
    if (productData.productName === "Hatch Sleep Membership + Restore device") {
      for (let i = 0; i < checkout.lineItems.length; i++) {
        if (
          checkout.lineItems[i].title ===
          "Hatch Sleep Membership + Restore device"
        ) {
          skipAddToCart = true;
        }
      }
    }
    //end JGunther 4/5/2022
    if (skipAddToCart) {
      showCart();
    } else {
      addToCart({
        checkoutId,
        quantity,
        variantId: productData.variantId,
        customAttributes: productData.customAttributes,
        skipAddToCart,
      });

      trackAddToShopifyCart({
        product: getProductName(
          productData.productName,
          productData.variantName
        ),
        variant: productData.variantName,
        currentQuantity: quantity,
        action: EVENT_PROPERTIES_VALUES.Add_To_Cart_Cta_Clicked,
        pageUrl,
        pageVariant,
        variantData: productData.variantData,
        productShopifyData,
        ...trackingProps,
      });
    }

    trackViewCart(checkout.lineItems);

    trackOpenCart({
      pageUrl,
      action: EVENT_PROPERTIES_VALUES.Add_To_Cart_Cta_Clicked,
      ...trackingProps,
    });
  };

export const ShopifyAddToCartCta = ({
  className,
  asLink,
  children,
  buttonProps,
  isDisabled,
  productShopifyData,
  variantId,
  checkout,
  ...rest
}) => {
  const {
    trackingActions: { trackAddToShopifyCart, trackOpenCart, trackViewCart },
    pageUrl,
    pageVariant,
  } = useContext(TrackingContext);
  const ctaOnClick = onClick({
    ...rest,
    trackAddToShopifyCart,
    trackOpenCart,
    trackViewCart,
    pageUrl,
    pageVariant,
    productShopifyData,
    variantId,
    checkout,
  });

  if (asLink) {
    const linkClasses = classNames("add_to_cart_link", className);

    return (
      <Link onClick={ctaOnClick} className={linkClasses} linkType="primary">
        {children}
      </Link>
    );
  }

  const btnClasses = classNames("add_to_cart_button", className);

  return (
    <Button
      className={btnClasses}
      onClick={ctaOnClick}
      isDisabled={isDisabled}
      {...buttonProps}
    >
      {children}
    </Button>
  );
};

ShopifyAddToCartCta.propTypes = {
  productShopifyData: PropTypes.object.isRequired,
  variantId: PropTypes.string,
  addToCart: PropTypes.func.isRequired,
  checkoutId: PropTypes.string,
  quantity: PropTypes.number.isRequired,
  isDisabled: PropTypes.bool,
  asLink: PropTypes.bool,
  className: PropTypes.any,
  children: PropTypes.any,
  buttonProps: PropTypes.object,
  trackingProps: PropTypes.shape({
    sectionName: PropTypes.string,
  }),
};

ShopifyAddToCartCta.defaultProps = {
  quantity: 1,
  asLink: false,
  children: "Add to Cart",
};

const mapStateToProps = (state) => {
  return {
    checkoutId: state.shop.checkout.id,
    checkout: state.shop.checkout,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    addToCart: (variantId, checkoutId, quantity) =>
      dispatch(addToCartRequest(variantId, checkoutId, quantity)),
    showCart: () => dispatch(showCart()),
  };
};

// For Builder.io, wrap the component in an HOC that has Shopify product data
// We need this because Builder pages don't have access to our
// Redux at render time & therefore won't have `productShopifyData` set.
const withShopifyProductData = (WrappedComponent) => {
  const ProductData = ({
    productKey,
    backgroundColor: color,
    isFullWidthOnMobile,
    ...rest
  }) => {
    // Get all Hatch collection data
    const shopifyData = useHatchProductCollection();

    // Find just the product data we need based on key (Ex. 'rest')
    const productShopifyData = shopifyData[productKey];
    const componentProps = {
      ...rest,
      productShopifyData,
      buttonProps: {
        color,
        size: isFullWidthOnMobile ? "mobileOnlyStretch" : "fixed",
      },
    };
    return <WrappedComponent {...componentProps} />;
  };

  ProductData.propTypes = {
    productKey: PropTypes.string.isRequired,
    backgroundColor: PropTypes.string,
    isFullWidthOnMobile: PropTypes.bool,
  };

  return ProductData;
};

const ConnectedShopifyAddToCartCta = connect(
  mapStateToProps,
  mapDispatchToProps
)(ShopifyAddToCartCta);

const AddToCartCtaWithProductData = withShopifyProductData(
  ConnectedShopifyAddToCartCta
);

export const ShopifyAddToCartBlock = withBuilder(AddToCartCtaWithProductData, {
  name: "Add to Cart Button",
  image: "https://hatchbaby.imgix.net/webassets/add-to-cart-icon-builder.svg",
  inputs: [
    {
      name: "children",
      type: "string",
      defaultValue: "Add to Cart",
    },
    {
      name: "productKey",
      type: "string",
      defaultValue: "rest",
      enum: Object.keys(HATCH_PRODUCT_HANDLES),
      helperText: "Unique key for the product",
    },
    {
      name: "backgroundColor",
      type: "string",
      defaultValue: "blue",
      enum: ["blue", "navy", "teal", "rose"],
    },
    {
      name: "isFullWidthOnMobile",
      type: "boolean",
      defaultValue: false,
      helperText: "Make the button 100% of the screen width on mobile",
    },
    {
      name: "asLink",
      type: "boolean",
      defaultValue: false,
      helperText: "Display button as a link",
    },
  ],
});

export default ConnectedShopifyAddToCartCta;
